/**
 * @description
 * TakeDepositDialogComponent provides a modal dialog for entering and validating
 * down payments (acomptes) on sales orders in PREPARATION state.
 * It features quick percentage presets, real-time VAT prorata breakdown calculation,
 * payment method selection (CB, Cash, Check, Wire) with conditional metadata inputs,
 * and secure dispatching of the TAKE_DEPOSIT event.
 *
 * Called via: MatDialog.open(TakeDepositDialogComponent, ...)
 *
 * Dependencies:
 * - QuotationService (`@app/sales/quotation/quotation.service`)
 * - ValuesService (`@app/shared/values.service`)
 * - FormatCurrencyPipe (`@app/shared/pipes/format-currency.pipe`)
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CommonModule, DecimalPipe } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { catchError, finalize, of } from 'rxjs';
import Swal from 'sweetalert2';

import { ValuesService } from '@app/common/values/values.service';
import { LoginService } from '@app/common/login.service';
import { CASH_NEEDS_DRAWER, cashFamilyOf, isPosStation } from '@app/sales/reglement/reglement.models';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { C_PAYMENT_METHOD } from '@app/shared/constants';
import { QuotationService } from '../quotation.service';

/**
 * Input payload passed to TakeDepositDialogComponent via MAT_DIALOG_DATA.
 */
export interface TakeDepositDialogData {
  orderId: number;
  remainder: number;
  amount_taxi?: number;
  lines?: any[];
  tax?: any[];
}

/**
 * Breakdown row representing estimated VAT per rate family.
 */
export interface VatBreakdownRow {
  vatRate: number;
  baseHt: number;
  tva: number;
  ttc: number;
}

@Component({
  selector: 'app-take-deposit-dialog',
  standalone: true,
  templateUrl: './take-deposit-dialog.component.html',
  styleUrls: ['./take-deposit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DecimalPipe,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    FormatCurrencyPipe,
  ],
})
export class TakeDepositDialogComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private quotationService = inject(QuotationService);
  private valuesService = inject(ValuesService);
  private loginService = inject(LoginService);

  /** Loading state during asynchronous submission */
  public isSubmitting: boolean = false;

  /** Available payment methods loaded from c_values category 7 */
  public paymentMethods: { id: number; name: string }[] = [];

  private cashIds: number[] = [];
  public readonly cashNeedsDrawer = CASH_NEEDS_DRAWER;

  /** Computed real-time VAT prorata breakdown rows */
  public vatBreakdown: VatBreakdownRow[] = [];

  /** Computed total HT from prorata */
  public totalHt: number = 0;

  /** Computed total TVA from prorata */
  public totalTva: number = 0;

  /** Quick percentage presets */
  public readonly percentagePresets: number[] = [0.2, 0.3, 0.5, 1.0];

  /** Plancher d'un acompte : 5 % du reste à charge et 10 €, ramené au reste s'il est plus petit ; contrôlé sur le montant (la part arrondie bloquait sans message). */
  public readonly minDepositPct = 5;
  public readonly minDepositFloor = 10;
  public minAmount = this.minDepositFloor;

  /** Erreur du montant affichée dès qu'il est invalide, sans attendre la sortie du champ : jamais de bouton grisé muet. */
  public readonly amountErrorMatcher: ErrorStateMatcher = { isErrorState: (control) => !!control?.invalid };

  /** Reactive form group for deposit parameters */
  public formGroup: FormGroup = new FormGroup({
    amount: new FormControl<number | null>(null, [Validators.required]),
    percent: new FormControl<number | null>(null, [Validators.max(100)]),
    paymentMethod: new FormControl<number>(1, [Validators.required]), // Default CB (1)
    checkNumber: new FormControl<string>(''),
    bankName: new FormControl<string>(''),
    cardRef: new FormControl<string>(''),
    transferRef: new FormControl<string>(''),
    note: new FormControl<string>(''),
  });

  constructor(
    public dialogRef: MatDialogRef<TakeDepositDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TakeDepositDialogData
  ) {}

  ngOnInit(): void {
    // Même arrondi que la saisie de la part : taper 5 % donne exactement ce minimum ; sous 10 € de reste, seul le reste entier passe
    this.minAmount = Math.min(this.data.remainder, Math.max(this.minDepositFloor, Math.round(this.data.remainder * this.minDepositPct) / 100));
    this.formGroup.get('amount')?.addValidators(Validators.min(this.minAmount));

    // Set max validator based on out-of-pocket remainder
    if (this.data.remainder > 0) {
      this.formGroup.get('amount')?.addValidators(Validators.max(this.data.remainder));
      // Default to 30% or remaining if smaller
      const defaultAmount = Math.min(this.data.remainder, Math.round(this.data.remainder * 0.3 * 100) / 100);
      this.formGroup.get('amount')?.setValue(defaultAmount > 0 ? defaultAmount : this.data.remainder);
    }

    // Subscribe to amount changes to recompute real-time VAT breakdown
    this.formGroup.get('amount')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((amount) => {
      this.syncPercentFromAmount(amount);
      this.computeVatBreakdown(amount || 0);
      this.cdr.markForCheck();
    });

    // Reverse binding: typing a percentage drives the amount (silently, no feedback loop)
    this.formGroup.get('percent')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((pct) => {
      this.syncAmountFromPercent(pct);
      this.cdr.markForCheck();
    });

    // Initial VAT computation
    this.syncPercentFromAmount(this.formGroup.get('amount')?.value);
    this.computeVatBreakdown(this.formGroup.get('amount')?.value || 0);

    // Load active payment methods
    this.loadPaymentMethods();
  }

  /**
   * Loads payment methods from c_values category 7 and filters out non-positives, inactive ones and AVOIR.
   */
  private loadPaymentMethods(): void {
    this.valuesService.getValues$(C_PAYMENT_METHOD).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((values: any[]) => {
      if (Array.isArray(values)) {
        // Exclude non-positive IDs, inactive methods (UNTDID 4461 means the store does not take) and AVOIR (id 4)
        this.paymentMethods = values.filter((v) => v.id > 0 && v.is_active !== false && v.id !== 4);
        this.cashIds = cashFamilyOf(values)?.ids ?? [];
        const current = this.formGroup.get('paymentMethod')?.value;
        if (this.paymentMethods.length > 0 && (!this.paymentMethods.some(m => m.id === current) || this.isCashLocked(current))) {
          this.formGroup.get('paymentMethod')?.setValue(this.paymentMethods.find(m => !this.isCashLocked(m.id))?.id ?? null);
        }
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * Quick preset button click handler (20%, 30%, 50%, 100%).
   *
   * @param pct Ratio between 0.01 and 1.0
   */
  public setAmountPreset(pct: number): void {
    this.formGroup.get('amount')?.setValue(this.presetAmount(pct));
    this.formGroup.get('amount')?.markAsDirty();
  }

  /** Montant TTC correspondant à un preset, borné au reste à charge. */
  private presetAmount(pct: number): number {
    return Math.min(this.data.remainder, Math.round(this.data.remainder * pct * 100) / 100);
  }

  /** Le preset `pct` (ratio 0.01 à 1.0) correspond-il au montant saisi ? Allume son bouton. */
  public isPresetActive(pct: number): boolean {
    const amount = Number(this.formGroup.get('amount')?.value);
    if (!amount || this.data.remainder <= 0) {
      return false;
    }
    return Math.abs(amount - this.presetAmount(pct)) < 0.005;
  }

  /** Montant -> pourcentage. `emitEvent: false` : la réciproque ne doit pas se redéclencher. */
  private syncPercentFromAmount(amount: number | null): void {
    const value = Number(amount);
    const pct = this.data.remainder > 0 && value > 0
      ? Math.round((value / this.data.remainder) * 10000) / 100
      : null;
    this.formGroup.get('percent')?.setValue(pct, { emitEvent: false });
  }

  /** Pourcentage -> montant. La TVA est recalculée ici, faute d'événement sur `amount`. */
  private syncAmountFromPercent(pct: number | null): void {
    if (this.data.remainder <= 0) {
      return;
    }
    const value = Number(pct);
    const amount = pct === null || pct === ('' as any) || isNaN(value) || value <= 0
      ? null
      : Math.round(this.data.remainder * value) / 100;
    this.formGroup.get('amount')?.setValue(amount, { emitEvent: false });
    this.formGroup.get('amount')?.markAsDirty();
    this.computeVatBreakdown(amount || 0);
  }

  /**
   * Computes real-time proportional VAT breakdown matching backend calculate_deposit_from_order logic.
   *
   * @param depositAmount Entered gross down payment amount (TTC)
   */
  public computeVatBreakdown(depositAmount: number): void {
    if (!depositAmount || depositAmount <= 0) {
      this.vatBreakdown = [];
      this.totalHt = 0;
      this.totalTva = 0;
      return;
    }

    // Group VAT rates from order lines or tax summary
    const rateTotals = new Map<number, { ttc: number; ht: number }>();
    const lines = this.data.lines || [];

    if (lines.length > 0) {
      for (const line of lines) {
        const rate = Number(line.vat_rate || 0);
        const ttc = Number(line.net_taxi || 0);
        const ht = Number(line.net_taxx || 0);
        const current = rateTotals.get(rate) || { ttc: 0, ht: 0 };
        current.ttc += ttc;
        current.ht += ht;
        rateTotals.set(rate, current);
      }
    } else if (this.data.tax && this.data.tax.length > 0) {
      for (const t of this.data.tax) {
        const rate = Number(t.vat_rate || 0);
        const ttc = Number(t.amount_taxi || 0);
        const ht = Number(t.amount_taxx || 0);
        const current = rateTotals.get(rate) || { ttc: 0, ht: 0 };
        current.ttc += ttc;
        current.ht += ht;
        rateTotals.set(rate, current);
      }
    }

    let totalOrderTtc = 0;
    for (const entry of rateTotals.values()) {
      totalOrderTtc += entry.ttc;
    }

    if (rateTotals.size === 0 || totalOrderTtc <= 0) {
      // Fallback single standard rate (20%)
      const ht = Math.round((depositAmount / 1.2) * 100) / 100;
      const tva = Math.round((depositAmount - ht) * 100) / 100;
      this.vatBreakdown = [{ vatRate: 20.0, baseHt: ht, tva: tva, ttc: depositAmount }];
      this.totalHt = ht;
      this.totalTva = tva;
      return;
    }

    const rows: VatBreakdownRow[] = [];
    let accumulatedTtc = 0;
    let accumulatedHt = 0;

    const entries = Array.from(rateTotals.entries()).sort((a, b) => b[1].ttc - a[1].ttc);

    for (let i = 0; i < entries.length; i++) {
      const [rate, bucket] = entries[i];
      const isLast = i === entries.length - 1;

      let rowTtc = 0;
      if (isLast) {
        rowTtc = Math.round((depositAmount - accumulatedTtc) * 100) / 100;
      } else {
        const share = bucket.ttc / totalOrderTtc;
        rowTtc = Math.round(depositAmount * share * 100) / 100;
        accumulatedTtc += rowTtc;
      }

      const rowHt = Math.round((rowTtc / (1 + rate / 100)) * 100) / 100;
      const rowTva = Math.round((rowTtc - rowHt) * 100) / 100;
      accumulatedHt += rowHt;

      rows.push({
        vatRate: rate,
        baseHt: rowHt,
        tva: rowTva,
        ttc: rowTtc,
      });
    }

    this.vatBreakdown = rows;
    this.totalHt = Math.round(accumulatedHt * 100) / 100;
    this.totalTva = Math.round((depositAmount - this.totalHt) * 100) / 100;
  }

  /**
   * Getter for selected payment method identifier.
   */
  get selectedPaymentMethodId(): number {
    return this.formGroup.get('paymentMethod')?.value ?? 0;
  }

  /** Hors caisse, la famille espèces reste listée mais grisée (JP 04/10) : ce poste n'a pas de tiroir-caisse. */
  public isCashLocked(method: number | null): boolean {
    return !isPosStation(this.loginService.currentstation) && this.cashIds.includes(Number(method));
  }

  /**
   * Submits the take deposit event to the backend.
   */
  public onSubmit(): void {
    if (this.formGroup.invalid || this.isSubmitting) {
      return;
    }

    const amount = Number(this.formGroup.get('amount')?.value);
    if (!amount || amount <= 0 || amount > this.data.remainder) {
      Swal.fire({
        title: 'Montant invalide',
        text: `Le montant de l'acompte doit être compris entre 0,01 € et le reste à charge (${this.data.remainder.toFixed(2)} €).`,
        icon: 'warning',
        confirmButtonText: 'Corriger',
      });
      return;
    }

    const paymentMethod = Number(this.formGroup.get('paymentMethod')?.value);
    // Back-stop : griser l'option ne ferme pas une méthode publique.
    if (this.isCashLocked(paymentMethod)) {
      Swal.fire({ title: 'Mode de paiement refusé', text: CASH_NEEDS_DRAWER, icon: 'warning', confirmButtonText: 'Corriger' });
      return;
    }
    const paymentData: any = {};
    if (paymentMethod === 3) {
      // Chèque
      paymentData.check_number = this.formGroup.get('checkNumber')?.value?.trim();
      paymentData.bank = this.formGroup.get('bankName')?.value?.trim();
    } else if (paymentMethod === 1) {
      // CB
      paymentData.card_ref = this.formGroup.get('cardRef')?.value?.trim();
    } else if (paymentMethod === 6) {
      // Virement
      paymentData.transfer_ref = this.formGroup.get('transferRef')?.value?.trim();
    }

    const note = this.formGroup.get('note')?.value?.trim();

    this.isSubmitting = true;
    this.cdr.markForCheck();

    this.quotationService
      .postTakeDepositEvent(this.data.orderId, amount, paymentMethod, paymentData, note)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          const serverMsg = err.error?.message || err.error || err.message || 'Erreur serveur inconnue';
          Swal.fire({
            title: 'Échec de l\'encaissement',
            text: `Impossible d'enregistrer l'acompte.\n${serverMsg}`,
            icon: 'error',
            confirmButtonText: 'Fermer',
          });
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((res) => {
        if (res) {
          Swal.fire({
            title: 'Acompte enregistré',
            text: `L'acompte de ${amount.toFixed(2)} € a été encaissé et la facture d'acompte a été scellée avec succès.`,
            icon: 'success',
            timer: 2500,
            showConfirmButton: false,
          });
          this.dialogRef.close({ success: true, amount });
        }
      });
  }

  /**
   * Cancels and dismisses dialog.
   */
  public onCancel(): void {
    this.dialogRef.close(null);
  }
}
